import React from "react";
import {
  Alert,
  Box,
  Button,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircleRounded,
  LockOutlined,
  LogoutRounded,
} from "@mui/icons-material";

import { brandThemeCssVars, DEFAULT_BRAND_THEME } from "../themeTokens";

const focusRingSx = {
  "&:focus-visible": {
    outline: "3px solid var(--brand-cta, #0f766e)",
    outlineOffset: 2,
  },
};

export default function ClientCheckoutAuthPanel({
  enabled,
  authenticated,
  displayName = "",
  locked = false,
  onLogin,
  onLogout,
  onRefresh,
  t = (key) => key,
  brandTheme = DEFAULT_BRAND_THEME,
}) {
  const [expanded, setExpanded] = React.useState(false);
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState("");

  if (!enabled) return null;

  if (authenticated) {
    return (
      <Box
        component="section"
        role="region"
        aria-label={t("skipline.clientSignedIn")}
        sx={{
          ...brandThemeCssVars(brandTheme),
          p: { xs: 1.5, sm: 1.75 },
          borderRadius: 2.5,
          border: "1px solid rgba(15,118,110,0.22)",
          bgcolor:
            "color-mix(in srgb, var(--brand-cta, #0f766e) 8%, var(--brand-surface, #fff))",
        }}
      >
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
            <Box
              aria-hidden="true"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                flexShrink: 0,
                color: "var(--brand-cta, #0f766e)",
                bgcolor:
                  "color-mix(in srgb, var(--brand-cta, #0f766e) 12%, transparent)",
              }}
            >
              <CheckCircleRounded fontSize="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={900}>
                {t("skipline.clientSignedIn")}
              </Typography>
              {displayName ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block" }}
                  noWrap
                >
                  {displayName}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          <Button
            variant="outlined"
            size="small"
            onClick={onLogout}
            disabled={locked || !onLogout}
            startIcon={<LogoutRounded fontSize="small" />}
            sx={{
              ...focusRingSx,
              minHeight: 44,
              borderRadius: 2,
              flexShrink: 0,
              fontWeight: 800,
            }}
          >
            {t("skipline.clientLogout")}
          </Button>
        </Stack>
      </Box>
    );
  }

  const submit = async (event) => {
    event.preventDefault();
    if (pending || !onLogin) return;
    setPending(true);
    setError("");
    try {
      const result = await onLogin({ username, password });
      if (!result?.ok) {
        setError(t("payment.clientLoginFailed"));
        return;
      }
      setUsername("");
      setPassword("");
      setExpanded(false);
      await onRefresh?.();
    } catch (requestError) {
      setError(t("payment.clientLoginFailed"));
    } finally {
      setPending(false);
    }
  };

  return (
    <Box
      component="section"
      role="region"
      aria-label={t("skipline.clientSignInForCheckout")}
      sx={{
        ...brandThemeCssVars(brandTheme),
        p: { xs: 1.5, sm: 1.75 },
        borderRadius: 2.5,
        border: "1px solid rgba(124,45,18,0.12)",
        bgcolor:
          "color-mix(in srgb, var(--brand-surface, #fff) 90%, transparent)",
      }}
    >
      <Stack spacing={1.25}>
        <Stack direction="row" spacing={1} alignItems="flex-start">
          <Box
            aria-hidden="true"
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              flexShrink: 0,
              color: "var(--brand-accent, #9a3412)",
              bgcolor:
                "color-mix(in srgb, var(--brand-primary, #f7b500) 14%, transparent)",
            }}
          >
            <LockOutlined fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={900}>
              {t("skipline.clientSignInForCheckout")}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25, lineHeight: 1.45 }}
            >
              {t("skipline.clientSignInHelp")}
            </Typography>
          </Box>
        </Stack>
        {!expanded ? (
          <Button
            variant="outlined"
            onClick={() => setExpanded(true)}
            disabled={locked}
            aria-expanded={false}
            aria-controls="skipline-client-login-form"
            sx={{
              ...focusRingSx,
              minHeight: 46,
              borderRadius: 2,
              fontWeight: 850,
            }}
          >
            {t("payment.clientSignIn")}
          </Button>
        ) : (
          <Box
            id="skipline-client-login-form"
            component="form"
            onSubmit={submit}
            aria-busy={pending}
          >
            <Stack spacing={1.25}>
              {error ? (
                <Alert id="skipline-client-login-error" severity="error">
                  {error}
                </Alert>
              ) : null}
              <TextField
                label={t("payment.clientUsername")}
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                required
                size="small"
                disabled={pending}
                autoFocus
                aria-describedby={
                  error ? "skipline-client-login-error" : undefined
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 46,
                    borderRadius: 2,
                  },
                }}
              />
              <TextField
                label={t("payment.clientPassword")}
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
                size="small"
                disabled={pending}
                aria-describedby={
                  error ? "skipline-client-login-error" : undefined
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    minHeight: 46,
                    borderRadius: 2,
                  },
                }}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={pending}
                  aria-busy={pending}
                  fullWidth
                  sx={{
                    ...focusRingSx,
                    minHeight: 46,
                    borderRadius: 2,
                    fontWeight: 850,
                  }}
                >
                  {pending
                    ? t("payment.clientSigningIn")
                    : t("payment.clientSignIn")}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    setExpanded(false);
                    setError("");
                  }}
                  disabled={pending}
                  fullWidth
                  sx={{
                    ...focusRingSx,
                    minHeight: 44,
                    borderRadius: 2,
                    fontWeight: 800,
                  }}
                >
                  {t("payment.close")}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
}
